import { useMemo } from 'react';
import type { RecommendedProductDTOType } from '../../store/stylesSearch/schemas';
import { useGetProductDepthByStyleNumbersQuery } from '../../store/stylesSearch/stylesSearchApi';

type ProductDepth = {
  inventoryDepth: number;
  jitDepth: number;
};

type SearchProductWithDepth = Pick<
  RecommendedProductDTOType,
  'size_depth_in_lk' | 'size_depth_in_lk_or_jit' | 'variants'
> & {
  inventoryDepth?: never;
  jitDepth?: never;
};

type DisplayProductWithDepth = {
  inventoryDepth?: number;
  jitDepth?: number;
};

const EMPTY_DEPTH_BY_STYLE_NUMBER: Record<string, ProductDepth> = {};

const countVariantsByAvailability = (
  product: Pick<RecommendedProductDTOType, 'variants'>,
  availabilityType: 'store' | 'jit',
) =>
  (product.variants || []).reduce((count, variant) => {
    const availabilityValues = (variant.availability ?? [])
      .map(value => String(value).toLowerCase().trim())
      .filter(Boolean);

    return availabilityValues.includes(availabilityType) ? count + 1 : count;
  }, 0);

export const getProductDepth = (
  product: SearchProductWithDepth | DisplayProductWithDepth,
): ProductDepth => {
  const inventoryDepthFromSummary =
    'inventoryDepth' in product && typeof product.inventoryDepth === 'number'
      ? product.inventoryDepth
      : 'size_depth_in_lk' in product && typeof product.size_depth_in_lk === 'number'
        ? product.size_depth_in_lk
        : undefined;

  const totalDepthFromSummary =
    'size_depth_in_lk_or_jit' in product && typeof product.size_depth_in_lk_or_jit === 'number'
      ? product.size_depth_in_lk_or_jit
      : undefined;

  const inventoryDepth =
    inventoryDepthFromSummary ??
    ('variants' in product ? countVariantsByAvailability(product, 'store') : 0);

  const jitDepth =
    'jitDepth' in product && typeof product.jitDepth === 'number'
      ? product.jitDepth
      : typeof totalDepthFromSummary === 'number'
        ? Math.max(totalDepthFromSummary - inventoryDepth, 0)
        : 'variants' in product
          ? countVariantsByAvailability(product, 'jit')
          : 0;

  return {
    inventoryDepth,
    jitDepth,
  };
};

export const ProductDepthRail = ({
  inventoryDepth,
  jitDepth,
}: {
  inventoryDepth?: number;
  jitDepth?: number;
}) => {
  if (typeof inventoryDepth !== 'number' && typeof jitDepth !== 'number') {
    return null;
  }

  return (
    <div className="px-2 rounded-b-sm uppercase text-justify text-xs font-medium text-[#FAF9F6] flex justify-between items-center pointer-events-none absolute inset-x-0 bottom-0 py-1 bg-[#121212]/40 backdrop-blur-lg">
      <span>inv : {inventoryDepth ?? 0}</span>
      <span>|</span>
      <span>jit : {jitDepth ?? 0}</span>
    </div>
  );
};

export const useProductDepthByStyleNumbers = (styleNumbers: Array<string>) => {
  const normalizedStyleNumbers = useMemo(
    () => Array.from(new Set(styleNumbers.map(value => String(value).trim()).filter(Boolean))),
    [styleNumbers],
  );

  const { data } = useGetProductDepthByStyleNumbersQuery(
    { styleNumbers: normalizedStyleNumbers },
    { skip: normalizedStyleNumbers.length === 0 },
  );

  return useMemo(
    () => data?.depthByStyleNumber ?? EMPTY_DEPTH_BY_STYLE_NUMBER,
    [data?.depthByStyleNumber],
  );
};
